import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { track, resetConsent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Footer() {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: "Subscribe",
        email,
        phone: "—",
        project_type: "Newsletter",
        message: "Newsletter subscription request",
      });
      toast.success("Subscribed! We'll be in touch.");
      track("newsletter_subscribe");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="section-footer"
      className="relative border-t border-[var(--border-dim)] bg-[var(--bg)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand + tagline */}
          <div className="md:col-span-5">
            <Logo variant="full" />
            <div className="mt-4 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-3)]">
              Development & Architecture
            </div>
            <p className="mt-6 max-w-sm text-[var(--text-2)] text-sm leading-relaxed">
              Helping small businesses modernize operations through websites,
              applications, automation, and AI-powered technology solutions.
            </p>
          </div>

          {/* Site Map */}
          <div className="md:col-span-3">
            <div className="overline mb-4">Site map</div>
            <ul className="space-y-2 font-mono-ui text-[11px] tracking-[0.15em] uppercase">
              {[
                ["home", "Home"],
                ["services", "Services"],
                ["process", "Process"],
                ["about", "About"],
                ["contact", "Contact"],
              ].map(([id, label]) => (
                <li key={id}>
                  <button
                    data-testid={`footer-link-${id}`}
                    onClick={() => scrollTo(id)}
                    className="text-[var(--text-2)] hover:text-[var(--vermilion)] transition-colors"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Subscribe */}
          <div className="md:col-span-4">
            <div className="overline mb-4">Subscribe</div>
            <p className="text-[var(--text-2)] text-sm leading-relaxed mb-4">
              Occasional notes on shipping smarter digital systems.
            </p>
            <form onSubmit={subscribe} data-testid="footer-subscribe-form" className="flex gap-2">
              <input
                data-testid="footer-subscribe-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 bg-[var(--bg)]/60 border border-[var(--border-dim)] rounded-sm px-3 py-2 font-mono-ui text-xs text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--vermilion)] transition-colors"
              />
              <button
                type="submit"
                data-testid="footer-subscribe-submit"
                disabled={loading}
                className="px-4 py-2 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] disabled:opacity-60 text-white text-xs font-medium tracking-wide rounded-sm transition-colors"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[var(--border-dim)] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)]">
          <div>© {year} KS DevArc · All rights reserved</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <button
              data-testid="footer-cookie-settings"
              onClick={resetConsent}
              className="hover:text-[var(--text-1)] transition-colors uppercase tracking-[0.22em]"
            >
              Cookie settings
            </button>
            <a href="/privacy" data-testid="footer-link-privacy" className="hover:text-[var(--text-1)] transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" data-testid="footer-link-terms" className="hover:text-[var(--text-1)] transition-colors">
              Terms of Use
            </a>
            <span className="text-[var(--lime)]">Crafted with intent</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
